import "moment-timezone";
import {
    EmployeeIdFileState,
    EmployeeIdFile,
    EmployeeIdFileResponse,
    FileUploadRequest
} from "@/employer/types";
import {
    FetchState
} from "@/shared/types";
import { downloadCsvFile } from "@/shared/fileDownload";
import * as userService from "@/shared/api/userService";
import * as storeHelper from "@/shared/storeHelper";
import { defineStore } from "pinia";
import _ from "lodash";
import { usePageSizeStore } from "@/shared/store/pageSize";
import moment from "moment";
import { useUserStore } from "@/shared/store/user";
import { useNotifierStore } from "@/shared/store/notifier";

export const initialState: EmployeeIdFileState = {
    fileFetchState: FetchState.Uninitialized,
    pageNumber: 1,
    pages: [],
    files: [],
    totalRecords: 0
};

function mapFileforDisplay (file): EmployeeIdFile {
    const dateTimeObject = moment.tz(moment.utc(file.dateUploaded), moment.tz.guess());
    return {
        employeeIdFileId: file.employeeIdFileId,
        employerId: file.employerId,
        fileName: file.fileName,
        dateUploaded: dateTimeObject.format("MM/DD/YYYY"),
        uploadStatus: file.uploadStatus,
        updatedUserCount: file.updatedEmployees || 0,
        uploadedBy: file.uploadedBy
    };
}

export const useEmployeeIdUpdaterStore = defineStore("employeeIdUpdater", {
    state: (): EmployeeIdFileState => (_.cloneDeep(initialState)),
    getters: {
        fileListLoaded: state => state.fileFetchState === FetchState.Loaded,
        fileListLoading: state => state.fileFetchState === FetchState.Loading,
        pageCount () {
            const pageSizeStore = usePageSizeStore();
            const pageSize = pageSizeStore.pageSize;
            return this.totalRecords < pageSize ? 1 : Math.floor((this.totalRecords + pageSize - 1) / pageSize);
        },
        filesForPage: state => (): EmployeeIdFile[] => {
            const pageObject = state.pages[state.pageNumber];
            if (typeof pageObject === "undefined") {
                return [];
            }
            return pageObject;
        }
    },
    actions: {
        setFileFetchState (fetchState: FetchState) {
            this.fileFetchState = fetchState;
        },
        addFiles (response: EmployeeIdFileResponse) {
            if (this.totalRecords !== response.totalRecords) {
                this.pages = [];
            }
            const pages = [ ...this.pages ];
            pages[this.pageNumber] = response.files.map(mapFileforDisplay);
            this.totalRecords = response.totalRecords;
            this.pages = pages;
        },
        clearUploadFilesState () {
            this.files = [];
            this.pageNumber = 1;
            this.pages = [];
            this.totalRecords = 0;
            this.fileFetchState = FetchState.Uninitialized;
        },
        setPageNumber (pageNumber: number) {
            this.pageNumber = pageNumber;
        },
        loadFileList (
            args: { employerId: number, pageNumber: number }) {
            return new Promise<void>(resolve => {
                const employeeIdUpdaterStore = useEmployeeIdUpdaterStore();
                employeeIdUpdaterStore.setPageNumber(args.pageNumber);
                const pageObject = employeeIdUpdaterStore.pages[employeeIdUpdaterStore.pageNumber || 1];
                if (typeof pageObject !== "undefined") {
                    resolve();
                    return;
                } else {
                    employeeIdUpdaterStore.setFileFetchState(FetchState.Loading);
                    const pageSizeStore = usePageSizeStore();
                    userService
                        .getEmployeeIdFiles(args.employerId, args.pageNumber, pageSizeStore.pageSize)
                        .then(result => {
                            employeeIdUpdaterStore.addFiles(result.data);
                            employeeIdUpdaterStore.setFileFetchState(FetchState.Loaded);
                        })
                        .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
                }
            });
        },
        reloadFileList (employerId: number) {
            return new Promise(resolve => {
                const employeeIdUpdaterStore = useEmployeeIdUpdaterStore();
                employeeIdUpdaterStore.clearUploadFilesState();
                employeeIdUpdaterStore.setFileFetchState(FetchState.Loading);
                const pageSizeStore = usePageSizeStore();
                userService
                    .getEmployeeIdFiles(employerId, employeeIdUpdaterStore.pageNumber, pageSizeStore.pageSize)
                    .then(result => {
                        employeeIdUpdaterStore.addFiles(result.data);
                        employeeIdUpdaterStore.setFileFetchState(FetchState.Loaded);
                    })
                    .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
            });
        },
        uploadFile (request: FileUploadRequest) {
            const userStore = useUserStore();
            const notifierStore = useNotifierStore();
            return userService.processEmployeeIdUpdateFile(userStore.employerId, request.file)
                .then(resp => {
                    notifierStore.setSuccess("Your file is processing, check the file status.");
                    this.setFileFetchState(FetchState.Uninitialized);
                })
                .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        downloadFileResult (args: { employerId: number, fileId: number }): Promise<void> {
            const notifierStore = useNotifierStore();
            return userService.downloadEmployeeIdUpdateFileResult(args.employerId, args.fileId).then(response => {
                const fileName = `employer-${args.employerId}-census-file-${args.fileId}-result.log`;
                downloadCsvFile(response.data, fileName);
            }).catch(() => {
                notifierStore.setDanger("Could not download the employee ID update results file at this time.");
            });
        },
    }
});